import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import {
  ArrowCircleDownIcon,
  ArrowCircleUpIcon,
  CalendarDotsIcon,
  ClockIcon,
  DownloadSimpleIcon,
  HashIcon,
} from '@phosphor-icons/react';
import toast from 'react-hot-toast';

import { OrderDocumentType, OrderDocumentValidationStatus } from 'src/types/enums';
import { OrderStatus, TrailerType, LoadingMethod } from 'src/types/enums';
import { nullthrows } from '../../../utils/invariant';
import { Breadcrumb } from '../../../components/Breadcrumb';
import { PageHeader } from '../../../components/PageHeader';
import { OrderStatusTag } from '../../order/components/OrderStatusTag';
import { DataField } from '../../../components/DataField';
import { formatDate, formatDateTime, formatInputTime, sortDate } from '../../../utils/date';
import { useTranslation } from '../../../contexts/translation-context';
import { decimateAndFormatNumber } from '../../../utils/number';
import { calculateLinesTotalExclVat } from '../../order/utils/price';
import { TrailerTypeIcon } from '../../order/pages/order/TrailerTypes';
import { LoadingMethodIcon } from '../../order/pages/order/LoadingMethods';
import { getDisplayError } from '@utils/get-display-error';
import { Card } from 'components/Card';
import { PageHeading } from 'components/PageHeading';
import { captureException } from '@sentry/react';
import { trackEvent } from '../../../utils/track-event';
import { Button } from 'components/button/Button';
import { useDownloadDocument } from 'src/app/document/hooks/useDownloadDocument';
import { FileUploaderButton } from 'src/app/document/components/FileUploaderButton';
import { MultiFileUploaderButton } from 'src/app/document/components/MultiFileUploaderButton';
import {
  linkCarrierCmrDocument,
  getCarrierPortalOrder,
  linkCarrierPortalOrderDocument,
  unlinkCarrierPortalOrderDocument,
  getCarrierPortalOrderChatMessages,
  sendCarrierPortalOrderChatMessage,
} from '../models';
import { useCarrierPortalSupplier } from '../CarrierPortalCarrierContext';
import { OrderRouteMap, OrderRouteStop } from '../../../components/map/OrderRouteMap';
import { SpinnerBlock } from '../../../components/Spinner';
import { useOrderMapLocation } from 'src/app/order/hooks/useOrderMapLocation';
import { useAuth } from '../../../contexts/auth-context';
import { OrderChat } from '../../../components/chat/OrderChat';
import { CarrierPortalCmrStatus } from './CarrierPortalCmrStatus';
import { CarrierPortalDocsUploadButtons } from './CarrierPortalDocsUploadButtons';
import {
  OrderDocumentValidationStatusToColor,
  OrderDocumentValidationStatusToTranslationKey,
  RequiredDocument,
  isCarrierUploadableDocumentType,
} from '../../order/utils/document-validation';
import { CarrierPortalRequiredDocsCard } from './CarrierPortalRequiredDocsCard';
import { Pill } from '../../../components/pill/Pill';

export const CarrierPortalOrderDetail = () => {
  const { i18n, lang } = useTranslation();
  const { me } = useAuth();
  const { carrierId } = useCarrierPortalSupplier();
  const { orderId: _orderId } = useParams<{ orderId: string }>();
  const orderId = nullthrows(_orderId, 'Order ID is required');

  const { downloadFile } = useDownloadDocument();

  // Fetch order with SWR
  const {
    data: order,
    mutate: refreshOrder,
    error: orderError,
  } = useSWR(['carrier-portal-order', carrierId, orderId, lang], () => getCarrierPortalOrder(carrierId, orderId, lang));

  const {
    driverLocation,
    driverHeading,
    telemetryLastSeenAt,
    trailerLocation,
    trailerHeading,
    trailerPlate,
    trailerLastSeenAt,
  } = useOrderMapLocation(orderId);

  if (orderError) {
    return (
      <>
        <PageHeader title={i18n('carrierPortal.orders.error')} />
        <div className="p-4 text-negative-text">{getDisplayError(orderError)}</div>
      </>
    );
  }

  if (!order) {
    return (
      <>
        <PageHeader title={i18n('carrierPortal.loading')} />
        <SpinnerBlock message={i18n('carrierPortal.planning.loadingOrders')} />
      </>
    );
  }

  const sustainabilityReportDocument = order.documents.find(
    (v) => v.type === OrderDocumentType.SustainabilityReport,
  )?.document;

  const executionDate = order.lines
    .map((l) => {
      return l.stops.filter((s) => s.type === 'Load').sort((a, b) => sortDate(a.date, b.date))[0]?.date;
    })
    .sort(sortDate)[0];
  const distance = Math.round(order.lines.reduce((acc, curr) => curr.distance + acc, 0) / 1000);
  const emissions = order.lines.reduce((acc, curr) => curr.emissions + acc, 0);

  const cmrDoc = order.documents.find((d) => d.type === 'CMR')?.document;
  const requiredDocuments = order.requiredDocuments as RequiredDocument[];
  // The generic "Documents" button offers every required type this customer
  // expects, so a carrier no longer has to guess that e.g. a weigh slip is
  // wanted next to the CMR.
  const uploadableRequiredTypes = requiredDocuments
    .map((d) => d.type as OrderDocumentType)
    .filter(isCarrierUploadableDocumentType);
  const handleDocumentUploaded = async (docType: OrderDocumentType) => {
    trackEvent('carrier_portal_required_doc_uploaded', {
      carrier_id: carrierId,
      order_id: order.id,
      doc_type: docType,
    });
    await refreshOrder();
  };
  const totalWeight = order.lines.reduce(
    (acc, curr) => curr.contents.reduce((acc, curr) => acc + curr.weight, 0) + acc,
    0,
  );

  // Prepare map data
  const routeCoordinates: [number, number][] = order.lines.flatMap((l) => l.polyline ?? []);
  const mapStops: OrderRouteStop[] = order.lines.flatMap((l) =>
    l.stops
      .filter((s) => s.location.coordinates)
      .map((s) => ({
        type: s.type as 'Load' | 'Unload',
        coordinates: s.location.coordinates as [number, number],
        name: s.location.name,
      })),
  );
  const hasMapData = routeCoordinates.length > 0 || mapStops.length > 0;

  return (
    <>
      <PageHeader title={order.orderNumber ?? i18n('draft')} />

      <PageHeading
        leftSide={
          <div className="flex items-center gap-4">
            <Breadcrumb
              parentItem={{
                name: i18n('carrierPortal.orders.orders'),
                to: '..',
              }}
              currentItem={order.orderNumber ?? i18n('draft')}
            />

            <div className="flex items-center gap-3">
              <OrderStatusTag status={order.status as OrderStatus} />
              {order.documentValidationStatus !== OrderDocumentValidationStatus.NoDocuments && (
                <Pill color={OrderDocumentValidationStatusToColor[order.documentValidationStatus]} size="sm">
                  {`${i18n('carrierPortal.orders.documentValidation')}: ${i18n(
                    OrderDocumentValidationStatusToTranslationKey[order.documentValidationStatus],
                  )}`}
                </Pill>
              )}
            </div>
          </div>
        }
        rightSide={
          <div className="flex gap-2">
            {cmrDoc ? (
              <FileUploaderButton
                buttonText={i18n('carrierPortal.orders.documents')}
                title={i18n('carrierPortal.orders.documents')}
                initialName={`${i18n('carrierPortal.orders.documents')} ${order.orderNumber ?? ''}`}
                file={cmrDoc}
                onSubmit={async (document) => {
                  await linkCarrierCmrDocument(carrierId, {
                    orderId: order.id,
                    documentId: document.id,
                  });
                  trackEvent('carrier_portal_cmr_uploaded', {
                    carrier_id: carrierId,
                    order_id: order.id,
                  });
                  await refreshOrder();
                }}
                disableUpload={order.status === 'Cancelled'}
              />
            ) : (
              <>
                {(order.hasRejectedCmrs || order.hasPendingCmrs) && (
                  <CarrierPortalCmrStatus
                    hasRejectedCmrs={order.hasRejectedCmrs}
                    hasPendingCmrs={order.hasPendingCmrs}
                  />
                )}
                {!order.hasPendingCmrs && (
                  <CarrierPortalDocsUploadButtons
                    carrierId={carrierId}
                    orderId={order.id}
                    types={uploadableRequiredTypes}
                    disableUpload={order.status === 'Cancelled'}
                    onUploaded={handleDocumentUploaded}
                  />
                )}
              </>
            )}

            <MultiFileUploaderButton
              buttonText={i18n('customerPortal.orders.attachments')}
              title={i18n('customerPortal.orders.attachments')}
              files={
                order.documents.filter((v) => v.type === OrderDocumentType.CustomerAttachment).map((d) => d.document) ||
                []
              }
              onSubmit={async (documents) => {
                await Promise.allSettled(
                  documents.map(async (doc) => {
                    try {
                      await linkCarrierPortalOrderDocument(
                        carrierId,
                        order.id,
                        doc.id,
                        OrderDocumentType.CustomerAttachment,
                      );
                      toast.success(
                        i18n('customerPortal.orders.uploadedAttachment', {
                          document: doc.name,
                        }),
                      );
                    } catch (err) {
                      captureException(err);
                      toast.error(
                        i18n('customerPortal.orders.uploadedAttachment', {
                          document: doc.name,
                          error: getDisplayError(err),
                        }),
                      );
                    }
                  }),
                );
                await refreshOrder();
              }}
              unlinkDocument={async (docId: string) => {
                try {
                  await unlinkCarrierPortalOrderDocument(
                    carrierId,
                    order.id,
                    docId,
                    OrderDocumentType.CustomerAttachment,
                  );
                  toast.success(i18n('customerPortal.orders.attachmentDeleted'));
                  await refreshOrder();
                } catch (err) {
                  captureException(err);
                  toast.error(
                    i18n('customerPortal.orders.couldNotDeleteAttachment', {
                      error: getDisplayError(err),
                    }),
                  );
                }
              }}
              disableUpload={order.status === 'Cancelled'}
            />
          </div>
        }
      />

      <div className="grid xl:grid-cols-2 gap-4 px-4">
        <div className="grid content-start gap-4">
          <Card title={i18n('customerPortal.order.overview')}>
            <div className="grid grid-cols-2 gap-4">
              <DataField title={i18n('carrierPortal.orders.orderRef')}>{order.orderNumber || '-'}</DataField>
              <DataField title={i18n('customerPortal.orders.creationDate')}>{formatDate(order.createdAt)}</DataField>
              {executionDate && (
                <DataField title={i18n('customerPortal.orders.executionDate')}>{formatDate(executionDate)}</DataField>
              )}
              {order.cancellationReason && (
                <DataField title={i18n('customerPortal.orders.cancellationReason')}>
                  {order.cancellationReason}
                </DataField>
              )}
            </div>

            <div className="flex flex-wrap gap-4 mt-4">
              {order.allowedTrailerTypes.map((trailerType) => {
                return (
                  <div
                    className="bg-surface-sunken rounded flex flex-col items-center justify-center p-2 px-4"
                    key={`${order.id}-${trailerType}`}
                  >
                    <TrailerTypeIcon type={trailerType as TrailerType} variant="regular" />
                  </div>
                );
              })}
              {order.loadingMethod && (
                <div className="bg-surface-sunken rounded flex flex-col items-center justify-center p-2 px-4">
                  <LoadingMethodIcon type={order.loadingMethod as LoadingMethod} variant="regular" />
                </div>
              )}
            </div>
          </Card>

          <CarrierPortalRequiredDocsCard
            carrierId={carrierId}
            orderId={order.id}
            requiredDocuments={requiredDocuments}
            hasOutstanding={order.outstandingRequiredDocuments.length > 0}
            disableUpload={order.status === 'Cancelled'}
            onUploaded={handleDocumentUploaded}
          />
        </div>

        <div className="grid gap-4">
          {order.lines.map((l) => {
            const saleTotal = calculateLinesTotalExclVat(l.purchases);

            return (
              <div key={l.id} className="grid gap-8">
                <Card title={i18n('customerPortal.orders.stops')}>
                  <div className="grid grid-cols-2 gap-4">
                    {l.stops.map((stop) => {
                      return (
                        <div key={stop.id}>
                          <div className="flex items-center gap-2 mb-2">
                            {stop.type === 'Load' ? (
                              <ArrowCircleUpIcon className="w-6 h-6 text-brand" />
                            ) : (
                              <ArrowCircleDownIcon className="w-6 h-6 text-brand" />
                            )}

                            <div className="text-fg-muted font-medium">
                              {stop.type === 'Load'
                                ? i18n('pages.orderSummary.load')
                                : i18n('pages.orderSummary.unload')}
                            </div>
                          </div>
                          <div className="mb-1">
                            <div>{stop.location.name}</div>
                            <div className="whitespace-nowrap">{`${stop.location.street} ${stop.location.streetNumber}`}</div>
                            <div>{`${stop.location.country}-${stop.location.postalCode}`}</div>
                          </div>
                          <div className="flex items-center">
                            <HashIcon className="w-4 h-4 mr-2" />
                            <div>{stop.reference || '-'}</div>
                          </div>
                          <div className="flex items-center">
                            <CalendarDotsIcon className="w-4 h-4 mr-2" />
                            <div>{formatDate(stop.date)}</div>
                          </div>
                          <div className="flex items-center">
                            <ClockIcon className="w-4 h-4 mr-2" />
                            <div className="whitespace-nowrap">{`${formatInputTime(stop.timeStart)} - ${formatInputTime(
                              stop.timeEnd,
                            )}`}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>

                {!!l.purchases.length && (
                  <Card title={i18n('carrierPortal.orders.purchases')}>
                    {l.purchases.map((purchase) => {
                      return (
                        <div key={purchase.id} className="grid grid-cols-4">
                          <div className="col-span-3 whitespace-nowrap">
                            {`${decimateAndFormatNumber(purchase.amount, 2)} x ${purchase.productType.nameNl}`}
                          </div>
                          <div className="whitespace-nowrap">
                            {`${decimateAndFormatNumber((purchase.amount * purchase.unitPrice) / 100, 2, {
                              decimalSeperator: ',',
                            })} EUR`}
                          </div>
                        </div>
                      );
                    })}

                    <div className="grid grid-cols-4 mb-2 border-t border-border pt-2 mt-2">
                      <div className="col-span-3 whitespace-nowrap font-medium">
                        {i18n('customerPortal.orders.total')}
                      </div>
                      <div className="whitespace-nowrap font-medium text-brand">
                        {`${decimateAndFormatNumber(saleTotal, 2, {
                          decimalSeperator: ',',
                        })} EUR`}
                      </div>
                    </div>
                  </Card>
                )}
              </div>
            );
          })}
        </div>

        {emissions > 0 && (
          <Card
            title={i18n('customerPortal.orders.sustainability')}
            rightHeading={
              <div className="flex justify-between items-center mb-2">
                <Button
                  color="primary"
                  isDisabled={!sustainabilityReportDocument}
                  iconLeft={<DownloadSimpleIcon className="button-icon" />}
                  onTrigger={() => (sustainabilityReportDocument ? downloadFile(sustainabilityReportDocument) : null)}
                >
                  {i18n('carrierPortal.orders.report')}
                </Button>
              </div>
            }
          >
            <div className="grid grid-cols-2 gap-4">
              <DataField title={i18n('customerPortal.orders.truck')}>Euro 6</DataField>
              <DataField title={i18n('customerPortal.orders.distance')}>{`${distance}km`}</DataField>
              <DataField title={i18n('customerPortal.orders.weight')}>{`${totalWeight / 100}kg`}</DataField>
              <DataField title={i18n('customerPortal.orders.emissions')}>{`${decimateAndFormatNumber(emissions, 3, {
                decimalSeperator: ',',
              })}kg co2eq`}</DataField>
            </div>
          </Card>
        )}

        {hasMapData && (
          <div className="xl:col-span-2">
            <Card title={i18n('carrierPortal.orders.route')}>
              <OrderRouteMap
                routeCoordinates={routeCoordinates}
                stops={mapStops}
                driverLocation={driverLocation}
                driverHeading={driverHeading}
                trailerLocation={trailerLocation}
                trailerHeading={trailerHeading}
                trailerLabel={trailerPlate}
              />
              {/* Which source the marker came from, when it is not the driver app: a telemetry position
                  is only as fresh as this page load, so it says when the truck last reported it. */}
              {telemetryLastSeenAt && (
                <p className="mt-2 text-sm text-fg-muted">
                  {i18n('liveTracking.lastSignal')}: {formatDateTime(telemetryLastSeenAt)}
                </p>
              )}
              {/* The trailer's own line, so the two markers are told apart: the plate names it, and its
                  timestamp says how old the position under its marker is. */}
              {trailerLastSeenAt && trailerPlate && (
                <p className="mt-2 text-sm text-fg-muted">
                  {i18n('liveTracking.trailer')}: {trailerPlate} · {i18n('liveTracking.lastSignal')}:{' '}
                  {formatDateTime(trailerLastSeenAt)}
                </p>
              )}
            </Card>
          </div>
        )}

        <div className="xl:col-span-2">
          <Card title={i18n('chat.title')}>
            <OrderChat
              orderId={orderId}
              fetchMessages={getCarrierPortalOrderChatMessages}
              sendMessage={sendCarrierPortalOrderChatMessage}
              currentUserId={me.id}
            />
          </Card>
        </div>
      </div>
    </>
  );
};
